// src/components/snapsite/CopyableInfoModal.tsx
"use client";

import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import type { CopyableBlock } from '@/types';
import { Landmark, Copy, Check, CopyPlus } from 'lucide-react';
import { Separator } from '@/components/ui/separator';

interface CopyableInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  blocks: CopyableBlock[];
}

const iconMap = {
  bank: Landmark,
  copy: Copy,
};

const CopyButton = ({ textToCopy }: { textToCopy: string }) => {
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const handleCopy = () => {
    navigator.clipboard.writeText(textToCopy).then(() => {
      setCopied(true);
      toast({ title: '¡Copiado!', description: 'El valor se ha copiado al portapapeles.' });
      setTimeout(() => setCopied(false), 2000);
    }).catch(err => {
      console.error("Error al copiar:", err);
      toast({ variant: 'destructive', title: 'Error', description: 'No se pudo copiar el valor.' });
    });
  };

  return (
    <Button variant="ghost" size="icon" onClick={handleCopy} aria-label="Copiar valor">
      {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
    </Button>
  );
};

const CopyableInfoModal: React.FC<CopyableInfoModalProps> = ({ isOpen, onClose, blocks }) => {
  const { toast } = useToast();
  
  const handleCopyAll = (block: CopyableBlock) => {
    const fieldsText = block.fields
        .filter(field => field.name && field.value) // Solo incluir campos con nombre y valor
        .map(field => `${field.name}: ${field.value}`)
        .join('\n');
    
    // Incluir el título del bloque en el texto a copiar
    const textToCopy = `${block.title}\n${fieldsText}`;
    
    navigator.clipboard.writeText(textToCopy).then(() => {
        toast({ title: '¡Bloque Copiado!', description: `Se ha copiado toda la información de "${block.title}".` });
    }).catch(err => {
        console.error("Error al copiar todo el bloque:", err);
        toast({ variant: 'destructive', title: 'Error', description: 'No se pudo copiar el bloque completo.' });
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl">Información para Copiar</DialogTitle>
          <DialogDescription>
            Copia fácilmente la información que necesitas.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4 space-y-6 max-h-[60vh] overflow-y-auto pr-2">
          {blocks.map((block, index) => {
            const Icon = iconMap[block.icon] || Copy;
            return (
              <div key={block.id} className="p-4 border rounded-lg bg-muted/30">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold flex items-center">
                    <Icon className="w-5 h-5 mr-2 text-primary" />
                    {block.title}
                  </h3>
                   <Button variant="outline" size="sm" onClick={() => handleCopyAll(block)}>
                        <CopyPlus className="w-4 h-4 mr-2" />
                        Copiar Todo
                    </Button>
                </div>
                <div className="space-y-3">
                  {block.fields.map((field, fieldIndex) => (
                    field.name && field.value && (
                      <div key={fieldIndex} className="flex justify-between items-center bg-background p-2 rounded-md">
                        <div>
                          <p className="text-xs text-muted-foreground">{field.name}</p>
                          <p className="text-sm font-mono break-all">{field.value}</p>
                        </div>
                        <CopyButton textToCopy={field.value} />
                      </div>
                    )
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CopyableInfoModal;
